import { useState, useCallback, useEffect, useMemo, useRef, type ReactNode } from "react";
import { useGT } from "gt-react";
import {
  useEditorStore,
  useServersStore,
  useResultStore,
  useAppStore,
  useQueriesStore,
  useQueryHistoryNavStore,
  useSharedSyncStore,
  useSchemaStore,
  executeQuery,
  cancelQuery,
  buildAuthHeaders,
  handleRuntimeSetupError,
  scheduleAutoSave,
  upsertSharedPaneDraft,
  clearSharedPaneDraft,
  resolveSharedQueryConflict,
  dismissSharedQueryConflict,
  navigateHistoryBack,
  navigateHistoryForward,
  saveCurrentQueryAsSaved,
  updateQueryInDb,
  introspectSchema,
} from "../store";
import type { RestMethod, RestContentType } from "../store";
import type { QueryRiskLevel } from "../persistence";
import { GraphQLEditor } from "./GraphQLEditor";
import { MongoEditor } from "./MongoEditor";
import { MongoVariablesPane } from "./MongoVariablesPane";
import { MongoOptionsPane, type MongoOptions } from "./MongoOptionsPane";
import { SqlEditor } from "./SqlEditor";
import { SqlParamsPane } from "./SqlParamsPane";
import { SqlOptionsPane, type SqlOptions } from "./SqlOptionsPane";
import { Play, Braces, ChevronLeft, ChevronRight, X, ChevronDown, Save, Plus, RefreshCw } from "lucide-react";
import { QueryParamsGrid } from "./QueryParamsGrid";
import { QueryRiskBar, QUERY_RISK_OPTIONS } from "./QueryRiskBar";
import { buildRestRequestUrl, getRestBaseUrl, resolveServerUrls } from "../serverUrls";
import { browserVaultProvider } from "../vault";
import { print, parse } from "graphql";
import { toast } from "./Toasts";
import { defaultQueryOperationConfig, type MongoOperation } from "@spuerhund/shared/targets";
import { buildRestPath } from "@spuerhund/shared/queryMetadata";
import {
  getQuerySyncAdoptionAction,
  queryDraftsEqual,
  toQueryDraftSnapshot,
  type QueryDraftSnapshot,
} from "../sharedSync";

const REST_METHODS: RestMethod[] = ["GET", "POST", "PUT", "PATCH", "DELETE", "HEAD", "OPTIONS"];
const METHODS_WITH_BODY = new Set(["POST", "PUT", "PATCH"]);
const NEW_CATEGORY_OPTION = "__new_category__";
const CONTENT_TYPES: { value: RestContentType; label: string }[] = [
  { value: "json", label: "JSON" },
  { value: "xml", label: "XML" },
  { value: "text", label: "Text" },
  { value: "form-urlencoded", label: "Form" },
];
const METHOD_COLORS: Record<string, string> = {
  GET: "text-success",
  POST: "text-warning",
  PUT: "text-info",
  PATCH: "text-accent",
  DELETE: "text-danger",
  HEAD: "text-zinc-400",
  OPTIONS: "text-zinc-400",
};

function useElapsedTime(startedAt: number | null) {
  const [elapsed, setElapsed] = useState<number | null>(null);
  useEffect(() => {
    if (startedAt === null) { setElapsed(null); return; }
    const tick = () => setElapsed(Math.round((performance.now() - startedAt) / 1000));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [startedAt]);
  return elapsed;
}

export function QueryEditor({
  headerAction,
}: {
  headerAction?: ReactNode;
} = {}) {
  const t = useGT();
  const queryText = useEditorStore((s) => s.queryText);
  const variables = useEditorStore((s) => s.variables);
  const headers = useEditorStore((s) => s.headers);
  const activeTab = useEditorStore((s) => s.activeTab);
  const requestType = useEditorStore((s) => s.requestType);
  const restMethod = useEditorStore((s) => s.restMethod);
  const restPath = useEditorStore((s) => s.restPath);
  const restParams = useEditorStore((s) => s.restParams);
  const restContentType = useEditorStore((s) => s.restContentType);
  const setQueryText = useEditorStore((s) => s.setQueryText);
  const setVariables = useEditorStore((s) => s.setVariables);
  const setHeaders = useEditorStore((s) => s.setHeaders);
  const setActiveTab = useEditorStore((s) => s.setActiveTab);
  const setRequestType = useEditorStore((s) => s.setRequestType);
  const setRestMethod = useEditorStore((s) => s.setRestMethod);
  const setRestPath = useEditorStore((s) => s.setRestPath);
  const setRestParams = useEditorStore((s) => s.setRestParams);
  const setRestContentType = useEditorStore((s) => s.setRestContentType);
  const activeServerId = useServersStore((s) => s.activeServerId);
  const isExecuting = useResultStore((s) => s.isExecuting);
  const executionStartedAt = useResultStore((s) => s.executionStartedAt);
  const activePaneId = useAppStore((s) => s.activePaneId);
  const elapsedSeconds = useElapsedTime(isExecuting ? executionStartedAt : null);
  const servers = useServersStore((s) => s.servers);
  const server = servers.find((s) => s.id === activeServerId);
  const isProd = !!server?.isProduction;
  const queries = useQueriesStore((s) => s.queries);
  const activeQueryId = useQueriesStore((s) => s.activeQueryId);
  const activeQuery = queries.find((q) => q.id === activeQueryId);
  const sharedServerSync = useSharedSyncStore((s) => (activeServerId ? s.servers[activeServerId] : undefined));
  const syncedQuerySnapshot = useSharedSyncStore((s) => (activeQueryId ? s.querySyncedSnapshots[activeQueryId] ?? null : null));
  const queryConflict = useSharedSyncStore((s) => (activeQueryId ? s.queryConflicts[activeQueryId] ?? null : null));
  const recentRemoteUpdateAt = useSharedSyncStore((s) => (activeQueryId ? s.recentRemoteUpdates[activeQueryId] ?? null : null));
  const schemaCache = useSchemaStore((s) => s.schemaCache);
  const activeServerSchema = activeServerId ? schemaCache[activeServerId] : null;
  const mongoCollectionNames = useMemo(() => {
    if (!activeServerSchema?.catalogJson || activeServerSchema.catalogKind !== "mongo-catalog") return [];
    try {
      const catalog = JSON.parse(activeServerSchema.catalogJson) as { collections?: { name: string }[] };
      return (catalog.collections ?? []).map((c) => c.name).sort((a, b) => a.localeCompare(b));
    } catch { return []; }
  }, [activeServerSchema?.catalogJson, activeServerSchema?.catalogKind]);
  const isRest = requestType === "rest";
  const isMongo = requestType === "mongo";
  const isSql = requestType === "sql";
  const hasBody = METHODS_WITH_BODY.has(restMethod);
  const savedCategoryOptions = useMemo(
    () =>
      Array.from(
        new Set(
          queries
            .filter((query) => !query.systemPreset && !query.pinnedAt && query.category?.trim())
            .map((query) => query.category!.trim()),
        ),
      ).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" })),
    [queries],
  );

  const restBaseUrl = server ? getRestBaseUrl(server) : "";

  // History navigation
  const queryHistory = useQueryHistoryNavStore((s) => s.queryHistory);
  const historyCursor = useQueryHistoryNavStore((s) => s.historyCursor);

  const canGoBack = queryHistory.length > 0 && (historyCursor === null || historyCursor < queryHistory.length - 1);
  const canGoForward = historyCursor !== null;
  const isViewingHistory = historyCursor !== null;

  const historyPositionLabel = historyCursor === null
    ? "live"
    : `${queryHistory.length - historyCursor} / ${queryHistory.length}`;

  const [curlCopied, setCurlCopied] = useState(false);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [saveName, setSaveName] = useState("");
  const [saveCategorySelection, setSaveCategorySelection] = useState<string>("Saved");
  const [saveCategory, setSaveCategory] = useState("Saved");
  const [queryRemarks, setQueryRemarks] = useState("");
  const [queryRiskLevel, setQueryRiskLevel] = useState<QueryRiskLevel>("normal");
  const [saveRemarks, setSaveRemarks] = useState("");
  const [saveRiskLevel, setSaveRiskLevel] = useState<QueryRiskLevel>("normal");
  const [mongoCollection, setMongoCollection] = useState("");
  const [mongoOperation, setMongoOperation] = useState<MongoOperation>("find");
  const [mongoOptions, setMongoOptions] = useState<MongoOptions>({});
  const [collectionDropdownOpen, setCollectionDropdownOpen] = useState(false);
  const [isRefreshingCatalog, setIsRefreshingCatalog] = useState(false);
  const collectionComboRef = useRef<HTMLDivElement>(null);
  const [sqlDefaultSchema, setSqlDefaultSchema] = useState("");
  const [sqlOptions, setSqlOptions] = useState<SqlOptions>({});
  const mongoIndexNames = useMemo(() => {
    if (!activeServerSchema?.catalogJson || activeServerSchema.catalogKind !== "mongo-catalog") return [];
    try {
      const catalog = JSON.parse(activeServerSchema.catalogJson) as {
        collections?: { name: string; indexes?: { name: string }[] }[];
      };
      const col = catalog.collections?.find((c) => c.name === mongoCollection);
      return (col?.indexes ?? []).map((i) => i.name).filter((n) => n !== "_id_");
    } catch { return []; }
  }, [activeServerSchema?.catalogJson, activeServerSchema?.catalogKind, mongoCollection]);
  const sqlSchemaNames = useMemo(() => {
    if (!activeServerSchema?.catalogJson || activeServerSchema.catalogKind !== "postgres-catalog") return [];
    try {
      const catalog = JSON.parse(activeServerSchema.catalogJson) as { schemas?: { name: string }[] };
      return (catalog.schemas ?? []).map((s) => s.name).sort();
    } catch { return []; }
  }, [activeServerSchema?.catalogJson, activeServerSchema?.catalogKind]);
  const [baseSnapshot, setBaseSnapshot] = useState<QueryDraftSnapshot | null>(null);
  const [isApplyingRemoteSync, setIsApplyingRemoteSync] = useState(false);
  const metadataSaveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const metadataQueryIdRef = useRef<string | null>(null);
  const metadataRemarksRef = useRef(queryRemarks);
  const metadataRiskLevelRef = useRef<QueryRiskLevel>(queryRiskLevel);
  const currentOperationConfig = requestType === "mongo"
    ? {
        kind: "mongo" as const,
        collection: mongoCollection,
        operation: mongoOperation,
      }
    : requestType === "sql"
      ? {
          kind: "sql" as const,
          ...(sqlDefaultSchema.trim() ? { defaultSchema: sqlDefaultSchema.trim() } : {}),
        }
      : undefined;
  const editorSnapshot = {
    queryText,
    variables,
    headers,
    remarks: queryRemarks,
    riskLevel: queryRiskLevel,
    requestType,
    ...(currentOperationConfig ? { operationConfig: currentOperationConfig } : {}),
    restMethod,
    restPath,
    restContentType,
    restParams,
  } as const;
  metadataQueryIdRef.current = activeQuery && !activeQuery.systemPreset ? activeQuery.id : null;
  metadataRemarksRef.current = queryRemarks;
  metadataRiskLevelRef.current = queryRiskLevel;

  const effectiveBaseSnapshot = baseSnapshot
    ?? (activeQuery && !activeQuery.systemPreset ? (syncedQuerySnapshot ?? toQueryDraftSnapshot(activeQuery)) : null);
  const isSharedSavedQuery = Boolean(activeServerId && activeQuery && !activeQuery.systemPreset && server?.sharedConfigId);
  const isLocalDirty = Boolean(
    !isApplyingRemoteSync
      && effectiveBaseSnapshot
      && activeQuery
      && !activeQuery.systemPreset
      && !queryDraftsEqual(effectiveBaseSnapshot, editorSnapshot),
  );
  const hasConflict = Boolean(queryConflict);
  const hasRecentRemoteUpdate = Boolean(recentRemoteUpdateAt && Date.now() - recentRemoteUpdateAt < 8_000);
  const isSavingCopy = Boolean(activeQuery && !activeQuery.systemPreset);
  const saveButtonLabel = isSavingCopy ? t("Kopie speichern") : t("Save");
  const saveButtonTitle = isSavingCopy ? t("Kopie speichern") : t("Save as saved query");
  const localSyncHint = isSharedSavedQuery && isLocalDirty ? t("Run to publish") : null;

  useEffect(() => {
    if (!showSaveDialog) return;
    const defaultExistingCategory = savedCategoryOptions[0] ?? "Saved";
    if (activeQuery && !activeQuery.systemPreset) {
      setSaveName(activeQuery.name);
      setSaveRemarks(queryRemarks);
      setSaveRiskLevel(queryRiskLevel);
      const initialCategory = activeQuery.category?.trim();
      if (initialCategory) {
        setSaveCategorySelection(initialCategory);
        setSaveCategory(initialCategory);
      } else {
        setSaveCategorySelection(NEW_CATEGORY_OPTION);
        setSaveCategory(defaultExistingCategory);
      }
    } else {
      setSaveName("");
      setSaveRemarks("");
      setSaveRiskLevel("normal");
      setSaveCategorySelection(savedCategoryOptions.includes("Saved") ? "Saved" : NEW_CATEGORY_OPTION);
      setSaveCategory(defaultExistingCategory);
    }
  }, [showSaveDialog, activeQuery, savedCategoryOptions]);

  useEffect(() => {
    if (!activeQuery || activeQuery.systemPreset) {
      setBaseSnapshot(null);
      setQueryRemarks("");
      setQueryRiskLevel("normal");
      // For adhoc queries, still try to restore operationConfig (e.g. collection name)
      const fallbackOperationConfig = activeQuery
        ? defaultQueryOperationConfig(activeQuery)
        : defaultQueryOperationConfig({ requestType });
      setMongoCollection(fallbackOperationConfig?.kind === "mongo" ? fallbackOperationConfig.collection : "");
      setMongoOperation(fallbackOperationConfig?.kind === "mongo" ? fallbackOperationConfig.operation : "find");
      setMongoOptions({});
      setSqlDefaultSchema(fallbackOperationConfig?.kind === "sql" ? fallbackOperationConfig.defaultSchema ?? "" : "");
      return;
    }
    setBaseSnapshot(syncedQuerySnapshot ?? toQueryDraftSnapshot(activeQuery));
    setQueryRemarks(activeQuery.remarks ?? "");
    setQueryRiskLevel(activeQuery.riskLevel ?? "normal");
    const operationConfig = defaultQueryOperationConfig(activeQuery);
    setMongoCollection(operationConfig?.kind === "mongo" ? operationConfig.collection : "");
    setMongoOperation(operationConfig?.kind === "mongo" ? operationConfig.operation : "find");
    // Restore mongo options from variables._options
    try {
      const vars = variables.trim() ? JSON.parse(variables) : {};
      setMongoOptions(vars._options ?? {});
    } catch { setMongoOptions({}); }
    setSqlDefaultSchema(operationConfig?.kind === "sql" ? operationConfig.defaultSchema ?? "" : "");
  }, [activeQuery?.id, requestType, syncedQuerySnapshot]);

  const persistOperationConfig = useCallback((next: typeof currentOperationConfig) => {
    if (!activeQuery || !next) return;
    void updateQueryInDb(activeQuery.id, { operationConfig: next }, { syncShared: false });
  }, [activeQuery]);

  useEffect(() => {
    if (!activeQuery || !isSharedSavedQuery) {
      clearSharedPaneDraft("primary");
      return;
    }

    upsertSharedPaneDraft("primary", {
      serverId: activeServerId,
      queryId: activeQuery.id,
      dirty: isLocalDirty,
      snapshot: editorSnapshot,
    });
  }, [
    activeQuery?.id,
    activeServerId,
    editorSnapshot.headers,
    editorSnapshot.queryText,
    editorSnapshot.remarks,
    editorSnapshot.riskLevel,
    editorSnapshot.requestType,
    editorSnapshot.restContentType,
    editorSnapshot.restMethod,
    editorSnapshot.restParams,
    editorSnapshot.restPath,
    editorSnapshot.variables,
    isLocalDirty,
    isSharedSavedQuery,
  ]);

  useEffect(() => () => clearSharedPaneDraft("primary"), []);

  useEffect(() => {
    if (!activeQuery || activeQuery.systemPreset || !effectiveBaseSnapshot) return;
    const nextSyncedSnapshot = syncedQuerySnapshot ?? toQueryDraftSnapshot(activeQuery);
    const action = getQuerySyncAdoptionAction({
      baseSnapshot: effectiveBaseSnapshot,
      editorSnapshot,
      syncedSnapshot: nextSyncedSnapshot,
    });
    if (action === "none") return;
    if (action === "applySynced") {
      setIsApplyingRemoteSync(true);
      setQueryText(activeQuery.queryText);
      setVariables(activeQuery.variables);
      setHeaders(activeQuery.headers);
      setQueryRemarks(activeQuery.remarks ?? "");
      setQueryRiskLevel(activeQuery.riskLevel ?? "normal");
      setRequestType(activeQuery.requestType ?? "graphql");
      setRestMethod(activeQuery.restMethod ?? "GET");
      setRestPath(activeQuery.restPath ?? "/");
      setRestParams(activeQuery.restParams ?? []);
      setRestContentType(activeQuery.restContentType ?? "json");
      upsertSharedPaneDraft("primary", {
        serverId: activeServerId,
        queryId: activeQuery.id,
        dirty: false,
        snapshot: nextSyncedSnapshot,
      });
    }
    setBaseSnapshot(nextSyncedSnapshot);
  }, [
    activeServerId,
    effectiveBaseSnapshot,
    editorSnapshot,
    activeQuery?.id,
    activeQuery?.updatedAt,
    syncedQuerySnapshot,
    setHeaders,
    setQueryText,
    setRequestType,
    setRestContentType,
    setRestMethod,
    setRestPath,
    setRestParams,
    setVariables,
  ]);

  useEffect(() => {
    if (!isApplyingRemoteSync || !effectiveBaseSnapshot) return;
    if (!queryDraftsEqual(effectiveBaseSnapshot, editorSnapshot)) return;
    setIsApplyingRemoteSync(false);
  }, [effectiveBaseSnapshot, editorSnapshot, isApplyingRemoteSync]);

  useEffect(() => {
    if (!activeQuery || activeQuery.systemPreset || !isSharedSavedQuery) return;
    if (hasConflict || isLocalDirty) return;
    const querySnapshot = toQueryDraftSnapshot(activeQuery);
    if (queryDraftsEqual(editorSnapshot, querySnapshot)) return;
    setIsApplyingRemoteSync(true);
    setQueryText(activeQuery.queryText);
    setVariables(activeQuery.variables);
    setHeaders(activeQuery.headers);
    setQueryRemarks(activeQuery.remarks ?? "");
    setQueryRiskLevel(activeQuery.riskLevel ?? "normal");
    setRequestType(activeQuery.requestType ?? "graphql");
    setRestMethod(activeQuery.restMethod ?? "GET");
    setRestPath(activeQuery.restPath ?? "/");
    setRestParams(activeQuery.restParams ?? []);
    setRestContentType(activeQuery.restContentType ?? "json");
    setBaseSnapshot(syncedQuerySnapshot ?? querySnapshot);
    upsertSharedPaneDraft("primary", {
      serverId: activeServerId,
      queryId: activeQuery.id,
      dirty: false,
      snapshot: syncedQuerySnapshot ?? querySnapshot,
    });
  }, [
    activeQuery,
    activeServerId,
    editorSnapshot,
    hasConflict,
    isLocalDirty,
    isSharedSavedQuery,
    syncedQuerySnapshot,
    setHeaders,
    setQueryText,
    setRequestType,
    setRestContentType,
    setRestMethod,
    setRestPath,
    setRestParams,
    setVariables,
  ]);

  const scheduleMetadataSave = useCallback((next: { remarks?: string; riskLevel?: QueryRiskLevel }) => {
    if (!activeQuery || activeQuery.systemPreset) return;
    if (metadataSaveTimeoutRef.current) clearTimeout(metadataSaveTimeoutRef.current);
    metadataSaveTimeoutRef.current = setTimeout(() => {
      metadataSaveTimeoutRef.current = null;
      void updateQueryInDb(activeQuery.id, {
        ...(next.remarks !== undefined ? { remarks: next.remarks } : {}),
        ...(next.riskLevel !== undefined ? { riskLevel: next.riskLevel } : {}),
      }, { syncShared: false });
    }, 500);
  }, [activeQuery]);

  useEffect(() => () => {
    if (!metadataSaveTimeoutRef.current) return;
    clearTimeout(metadataSaveTimeoutRef.current);
    metadataSaveTimeoutRef.current = null;
    if (!metadataQueryIdRef.current) return;
    void updateQueryInDb(metadataQueryIdRef.current, {
      remarks: metadataRemarksRef.current,
      riskLevel: metadataRiskLevelRef.current,
    }, { syncShared: false });
  }, []);

  const handleRemarksChange = useCallback((value: string) => {
    setQueryRemarks(value);
    scheduleMetadataSave({ remarks: value, riskLevel: queryRiskLevel });
  }, [queryRiskLevel, scheduleMetadataSave]);

  const handleRiskLevelChange = useCallback((value: QueryRiskLevel) => {
    setQueryRiskLevel(value);
    scheduleMetadataSave({ remarks: queryRemarks, riskLevel: value });
  }, [queryRemarks, scheduleMetadataSave]);

  const handleSaveQuery = useCallback(async () => {
    try {
      if (activeQuery && currentOperationConfig) {
        await updateQueryInDb(activeQuery.id, { operationConfig: currentOperationConfig }, { syncShared: false });
      }
      const resolvedCategory = saveCategorySelection === NEW_CATEGORY_OPTION
        ? saveCategory.trim()
        : saveCategorySelection;
      const input: Parameters<typeof saveCurrentQueryAsSaved>[0] = {
        name: saveName,
        saveTo: "category",
        category: resolvedCategory,
        remarks: saveRemarks,
        riskLevel: saveRiskLevel,
      };
      await saveCurrentQueryAsSaved(input);
      setShowSaveDialog(false);
      toast("Query saved", "success");
    } catch (error) {
      toast(error instanceof Error ? error.message : String(error), "error");
    }
  }, [activeQuery, currentOperationConfig, saveCategory, saveCategorySelection, saveName, saveRemarks, saveRiskLevel]);

  const handleKeepLocalVersion = useCallback(async () => {
    if (!activeQuery) return;
    try {
      const resolved = await resolveSharedQueryConflict(activeQuery.id, "pushLocal", editorSnapshot);
      if (resolved.appliedSnapshot) {
        setBaseSnapshot(resolved.appliedSnapshot);
        upsertSharedPaneDraft("primary", {
          serverId: activeServerId,
          queryId: activeQuery.id,
          dirty: false,
          snapshot: resolved.appliedSnapshot,
        });
      }
      toast("Local version pushed", "success");
    } catch (error) {
      toast(error instanceof Error ? error.message : String(error), "error");
    }
  }, [activeQuery, activeServerId, editorSnapshot]);

  const handleUseServerVersion = useCallback(async () => {
    if (!activeQuery) return;
    try {
      const resolved = await resolveSharedQueryConflict(activeQuery.id, "useRemote");
      if (resolved.appliedSnapshot) {
        setQueryText(resolved.appliedSnapshot.queryText);
        setVariables(resolved.appliedSnapshot.variables);
        setHeaders(resolved.appliedSnapshot.headers);
        setRequestType(resolved.appliedSnapshot.requestType ?? "graphql");
        setRestMethod(resolved.appliedSnapshot.restMethod ?? "GET");
        setRestPath(resolved.appliedSnapshot.restPath ?? "/");
        setRestParams(resolved.appliedSnapshot.restParams ?? []);
        setRestContentType(resolved.appliedSnapshot.restContentType ?? "json");
        setBaseSnapshot(resolved.appliedSnapshot);
        upsertSharedPaneDraft("primary", {
          serverId: activeServerId,
          queryId: activeQuery.id,
          dirty: false,
          snapshot: resolved.appliedSnapshot,
        });
      }
      toast("Server version applied", "success");
    } catch (error) {
      toast(error instanceof Error ? error.message : String(error), "error");
    }
  }, [activeQuery, activeServerId, setHeaders, setQueryText, setRequestType, setRestContentType, setRestMethod, setRestPath, setRestParams, setVariables]);

  const handleDismissConflict = useCallback(() => {
    if (!activeQuery) return;
    dismissSharedQueryConflict(activeQuery.id);
  }, [activeQuery]);

  const handleCopyAsCurl = useCallback(async () => {
    if (!activeServerId) return;
    if (!server) return;

    try {
      if (isMongo || isSql) {
        toast(t("Copy as cURL is only available for HTTP-based queries."), "error");
        return;
      }
      if (isRest) {
        const resolvedUrls = await resolveServerUrls(server, browserVaultProvider, { serverId: server.id });
        const fullUrl = buildRestRequestUrl(
          { baseUrl: resolvedUrls.baseUrl, restPath: resolvedUrls.restPath },
          buildRestPath(restPath, restParams),
        );
        const allHeaders: Record<string, string> = await buildAuthHeaders(server);
        if (hasBody) allHeaders["Content-Type"] = restContentType === "json" ? "application/json" : restContentType === "xml" ? "application/xml" : restContentType === "form-urlencoded" ? "application/x-www-form-urlencoded" : "text/plain";
        try {
          const parsed = JSON.parse(headers);
          if (typeof parsed === "object" && parsed !== null) Object.assign(allHeaders, parsed);
        } catch { /* ignore */ }

        const headerFlags = Object.entries(allHeaders).map(([k, v]) => `-H '${k}: ${v}'`).join(" \\\n  ");
        let curl = `curl -X ${restMethod} '${fullUrl}'`;
        if (headerFlags) curl += ` \\\n  ${headerFlags}`;
        if (hasBody && queryText.trim()) curl += ` \\\n  -d '${queryText.replace(/'/g, "'\\''")}'`;

        await navigator.clipboard.writeText(curl);
      } else {
        if (!queryText.trim()) return;
        const allHeaders: Record<string, string> = {
          "Content-Type": "application/json",
          ...(await buildAuthHeaders(server)),
        };
        try {
          const parsed = JSON.parse(headers);
          if (typeof parsed === "object" && parsed !== null) Object.assign(allHeaders, parsed);
        } catch { /* ignore */ }

        let parsedVars: Record<string, unknown> | undefined;
        try {
          const v = JSON.parse(variables);
          if (typeof v === "object" && v !== null && Object.keys(v).length > 0) parsedVars = v;
        } catch { /* ignore */ }

        const body = JSON.stringify({ query: queryText, ...(parsedVars ? { variables: parsedVars } : {}) });
        const headerFlags = Object.entries(allHeaders).map(([k, v]) => `-H '${k}: ${v}'`).join(" \\\n  ");
        const { graphqlEndpoint: graphqlUrl } = await resolveServerUrls(
          server,
          browserVaultProvider,
          { serverId: server.id },
        );
        const curl = `curl -X POST '${graphqlUrl}' \\\n  ${headerFlags} \\\n  -d '${body.replace(/'/g, "'\\''")}'`;
        await navigator.clipboard.writeText(curl);
      }

      setCurlCopied(true);
      setTimeout(() => setCurlCopied(false), 1500);
    } catch (error) {
      const message = handleRuntimeSetupError(error);
      if (message !== "Vault is locked. Unlock it in Secrets & Config.") {
        toast(`Copy as cURL failed: ${message}`, "error");
      }
    }
  }, [activeServerId, queryText, variables, headers, isMongo, isRest, isSql, restMethod, restPath, restParams, restContentType, hasBody, server, t]);

  const handleExecute = useCallback(async () => {
    if (!activeServerId) return;
    if ((requestType === "graphql" || requestType === "sql") && !queryText.trim()) return;
    if (metadataSaveTimeoutRef.current) {
      clearTimeout(metadataSaveTimeoutRef.current);
      metadataSaveTimeoutRef.current = null;
    }
    if (activeQuery && !activeQuery.systemPreset) {
      await updateQueryInDb(activeQuery.id, {
        ...(currentOperationConfig ? { operationConfig: currentOperationConfig } : {}),
        remarks: queryRemarks,
        riskLevel: queryRiskLevel,
      }, { syncShared: false });
    }
    await executeQuery();
  }, [activeQuery, activeServerId, currentOperationConfig, queryRemarks, queryRiskLevel, queryText, requestType]);

  const handlePrettify = useCallback(() => {
    if (isRest) {
      // Only prettify JSON body
      if (activeTab === "query" && restContentType === "json" && queryText.trim()) {
        try {
          const formatted = JSON.stringify(JSON.parse(queryText), null, 2);
          setQueryText(formatted);
          scheduleAutoSave();
        } catch { /* invalid JSON */ }
      } else if (activeTab === "headers") {
        try {
          const formatted = JSON.stringify(JSON.parse(headers), null, 2);
          setHeaders(formatted);
          scheduleAutoSave();
        } catch { /* invalid JSON */ }
      }
    } else if (isMongo) {
      if (activeTab === "query") {
        try {
          const formatted = JSON.stringify(JSON.parse(queryText), null, 2);
          setQueryText(formatted);
          scheduleAutoSave();
        } catch { /* invalid JSON */ }
      } else {
        const raw = activeTab === "variables" ? variables : headers;
        try {
          const formatted = JSON.stringify(JSON.parse(raw), null, 2);
          if (activeTab === "variables") setVariables(formatted);
          else setHeaders(formatted);
          scheduleAutoSave();
        } catch { /* invalid JSON */ }
      }
    } else {
      if (activeTab === "query") {
        try {
          const formatted = print(parse(queryText));
          setQueryText(formatted);
          scheduleAutoSave();
        } catch { /* invalid query */ }
      } else {
        const raw = activeTab === "variables" ? variables : headers;
        try {
          const formatted = JSON.stringify(JSON.parse(raw), null, 2);
          if (activeTab === "variables") setVariables(formatted);
          else setHeaders(formatted);
          scheduleAutoSave();
        } catch { /* invalid JSON */ }
      }
    }
  }, [activeTab, queryText, variables, headers, setQueryText, setVariables, setHeaders, isMongo, isRest, restContentType]);

  useEffect(() => {
    if (activePaneId !== "primary") return;
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
        if (e.defaultPrevented) return;
        e.preventDefault();
        handleExecute();
      }
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && (e.key === "f" || e.key === "F")) {
        if (e.defaultPrevented) return;
        e.preventDefault();
        handlePrettify();
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [activePaneId, handleExecute, handlePrettify]);

  // Param count for tab badge
  const paramCount = restParams.filter((p) => p.key.trim()).length;

  // Tabs: REST hides Variables, adds Params. Mongo replaces Headers with Options.
  const tabs = isRest
    ? [
        { id: "query" as const, label: "Body" },
        { id: "params" as const, label: paramCount > 0 ? `Params (${paramCount})` : "Params" },
        { id: "headers" as const, label: "Headers" },
      ]
    : isMongo
      ? [
          { id: "query" as const, label: "Query" },
          { id: "variables" as const, label: "Variables" },
          { id: "options" as const, label: "Options" },
        ]
      : isSql
        ? [
            { id: "query" as const, label: "Query" },
            { id: "params" as const, label: "Params" },
            { id: "options" as const, label: "Options" },
          ]
        : [
            { id: "query" as const, label: "Query" },
            { id: "variables" as const, label: "Variables" },
            { id: "headers" as const, label: "Headers" },
          ];

  // Reset invalid tabs when switching request type
  useEffect(() => {
    if (isRest && activeTab === "variables") {
      setActiveTab("query");
    }
    if (!isRest && !isSql && activeTab === "params") {
      setActiveTab("query");
    }
    if ((isMongo || isSql) && activeTab === "headers") {
      setActiveTab("query");
    }
    if (!isMongo && !isSql && activeTab === "options") {
      setActiveTab("query");
    }
    if (isSql && activeTab === "variables") {
      setActiveTab("query");
    }
  }, [isRest, isMongo, isSql, activeTab, setActiveTab]);

  const currentValue =
    activeTab === "query"
      ? queryText
      : activeTab === "variables"
        ? variables
        : headers;

  const handleChange = (value: string) => {
    if (activeTab === "query") setQueryText(value);
    else if (activeTab === "variables") setVariables(value);
    else setHeaders(value);
    scheduleAutoSave();
  };

  const handleMongoOptionsChange = useCallback((opts: MongoOptions) => {
    setMongoOptions(opts);
    // Persist options into the variables field as JSON with _options key
    try {
      const currentVars = variables.trim() ? JSON.parse(variables) : {};
      const next = { ...currentVars, _options: opts };
      setVariables(JSON.stringify(next, null, 2));
      scheduleAutoSave();
    } catch {
      setVariables(JSON.stringify({ _options: opts }, null, 2));
      scheduleAutoSave();
    }
  }, [variables, setVariables]);

  const handleSqlOptionsChange = useCallback((opts: SqlOptions) => {
    setSqlOptions(opts);
    // Update defaultSchema in operation config if changed
    if (opts.defaultSchema !== sqlDefaultSchema) {
      const schema = opts.defaultSchema ?? "";
      setSqlDefaultSchema(schema);
      persistOperationConfig({
        kind: "sql",
        ...(schema.trim() ? { defaultSchema: schema.trim() } : {}),
      });
    }
    scheduleAutoSave();
  }, [sqlDefaultSchema, persistOperationConfig]);

  const placeholder =
    isRest
      ? activeTab === "query"
        ? hasBody ? '{"key": "value"}' : ""
        : '{"Authorization": "Bearer ..."}'
      : activeTab === "query"
        ? isMongo
          ? '{"field": "value"}'
          : isSql
            ? "select * from table where id = $1"
            : t("Write your GraphQL query here")
        : activeTab === "variables"
          ? '{"key": "value"}'
          : '{"Authorization": "Bearer ..."}';

  // Editor mode (used by GraphQLEditor — mongo has its own MongoEditor)
  const editorMode: "graphql" | "json" | "text" = isRest
    ? (activeTab === "query"
        ? (restContentType === "json" ? "json" : "text")
        : "json")
    : activeTab === "query"
      ? isSql
        ? "text"
        : "graphql"
      : "json";

  // Whether prettify makes sense
  const canPrettify = isRest
    ? (activeTab === "query" && restContentType === "json") || activeTab === "headers"
    : !isSql;

  const syncDot = !isSharedSavedQuery
    ? null
    : hasConflict
      ? { label: t("Conflict"), className: "bg-danger" }
      : isLocalDirty
        ? { label: t("Local only"), className: "bg-warning" }
        : sharedServerSync?.pushing
          ? { label: t("Publishing..."), className: "bg-warning" }
          : sharedServerSync?.connection === "offline"
            ? { label: t("Offline"), className: "bg-danger" }
            : hasRecentRemoteUpdate
              ? { label: t("Remote update"), className: "bg-success" }
              : { label: t("Synced"), className: "bg-success" };
  const remoteActorHint = isSharedSavedQuery
    && activeQuery
    && hasRecentRemoteUpdate
    && sharedServerSync?.recentRemoteQueryIds.includes(activeQuery.id)
    && sharedServerSync.lastRemoteActor
    ? t("{actor} is also here", { actor: sharedServerSync.lastRemoteActor })
    : null;

  return (
    <div className="flex flex-1 flex-col border-b border-border overflow-hidden">
      {/* Name header */}
      {activeQuery && (
        <div className="border-b border-border bg-surface-raised">
          <div className="flex shrink-0 items-center justify-between gap-2 px-3 py-1">
            <div className="flex min-w-0 items-center gap-2">
              {syncDot && (
                <span
                  className={`h-2 w-2 shrink-0 rounded-full ${syncDot.className}`}
                  title={syncDot.label}
                  aria-label={syncDot.label}
                />
              )}
              <span className="truncate text-xs font-medium text-zinc-300" title={activeQuery.name}>
                {activeQuery.name}
              </span>
              {localSyncHint && (
                <span className="truncate text-[11px] text-warning" title={localSyncHint}>
                  ({localSyncHint})
                </span>
              )}
              {remoteActorHint && (
                <span className="truncate text-[11px] text-zinc-500" title={remoteActorHint}>
                  ({remoteActorHint})
                </span>
              )}
            </div>
            <div className="flex items-center gap-1.5">
              {activeServerId && (
                <button
                  onClick={() => setShowSaveDialog(true)}
                  className="flex items-center gap-1 rounded border border-zinc-700 px-2 py-1 text-xs text-zinc-400 hover:border-zinc-500 hover:text-zinc-200 transition-colors"
                  title={saveButtonTitle}
                >
                  <Save size={12} />
                  <span>{saveButtonLabel}</span>
                </button>
              )}
              {headerAction ?? (
                <button
                  disabled
                  className="flex shrink-0 items-center gap-1 rounded border border-zinc-700 px-2 py-1 text-xs text-zinc-600 opacity-40"
                >
                  <Plus size={12} />
                  <span>{t("Panel")}</span>
                </button>
              )}
            </div>
          </div>
        {hasConflict && queryConflict && (
            <div className="flex flex-wrap items-center gap-2 border-t border-danger/20 bg-danger-bg px-3 py-2">
              <div className="text-[11px] text-danger">
                {t("Remote update by {actor} is waiting.", {
                  actor: queryConflict.updatedByDisplayName || queryConflict.updatedByClientId || t("someone"),
                })}
                {queryConflict.remoteDeleted
                  ? ` ${t("The server deleted this query.")}`
                  : ` ${t("Keep your local work or use the server version.")}`}
              </div>
              <div className="ml-auto flex items-center gap-2">
                <button
                  onClick={() => void handleKeepLocalVersion()}
                  className="rounded bg-brand px-2.5 py-1 text-[11px] font-semibold text-zinc-950 hover:bg-brand-light"
                >
                  {t("Push mine")}
                </button>
                <button
                  onClick={() => void handleUseServerVersion()}
                  className="rounded border border-danger/30 px-2.5 py-1 text-[11px] font-medium text-danger hover:bg-danger-bg/80"
                >
                  {t("Use server version")}
                </button>
                <button
                  onClick={handleDismissConflict}
                  className="text-[11px] text-zinc-500 hover:text-zinc-300"
                >
                  {t("Later")}
                </button>
              </div>
          </div>
        )}
      </div>
      )}
      {/* REST URL bar */}
      {isRest && (
        <div className="flex shrink-0 items-center gap-1.5 border-b border-border bg-surface-raised px-2 py-1.5">
          {/* Method dropdown */}
          <div className="relative">
            <select
              value={restMethod}
              onChange={(e) => { setRestMethod(e.target.value as RestMethod); scheduleAutoSave(); }}
              className={`appearance-none rounded border border-zinc-700 bg-surface py-0.5 pl-2 pr-6 text-xs font-bold ${METHOD_COLORS[restMethod] ?? "text-zinc-400"} cursor-pointer hover:border-zinc-500 focus:border-accent focus:outline-none`}
            >
              {REST_METHODS.map((m) => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>
            <ChevronDown size={10} className="pointer-events-none absolute right-1.5 top-1/2 -translate-y-1/2 text-zinc-500" />
          </div>

          {/* Base URL label */}
          <span className="shrink-0 text-[11px] text-zinc-600 font-mono">{restBaseUrl}</span>

          {/* Path input */}
          <input
            type="text"
            value={restPath}
            onChange={(e) => { setRestPath(e.target.value); scheduleAutoSave(); }}
            placeholder={t("/path")}
            className="flex-1 rounded border border-zinc-700 bg-surface px-2 py-0.5 font-mono text-xs text-zinc-200 placeholder:text-zinc-600 focus:border-accent focus:outline-none"
          />
        </div>
      )}

      {(isMongo || isSql) && (
        <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-border bg-surface-raised px-2 py-1.5">
          {isMongo ? (
            <>
              <span className="rounded border border-zinc-700 bg-surface px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-zinc-400">
                Mongo
              </span>
              {/* Collection combobox */}
              <div ref={collectionComboRef} className="relative min-w-[10rem] flex-1">
                <input
                  type="text"
                  value={mongoCollection}
                  onChange={(event) => {
                    const next = event.target.value;
                    setMongoCollection(next);
                    persistOperationConfig({
                      kind: "mongo",
                      collection: next,
                      operation: mongoOperation,
                    });
                    if (!collectionDropdownOpen && mongoCollectionNames.length > 0) {
                      setCollectionDropdownOpen(true);
                    }
                  }}
                  onFocus={() => {
                    if (mongoCollectionNames.length > 0) setCollectionDropdownOpen(true);
                  }}
                  onBlur={(e) => {
                    // Close dropdown unless clicking inside it
                    if (collectionComboRef.current?.contains(e.relatedTarget as Node)) return;
                    setCollectionDropdownOpen(false);
                  }}
                  placeholder={mongoCollectionNames.length > 0 ? t("collection") : t("collection (explore schema for suggestions)")}
                  className="w-full rounded border border-zinc-700 bg-surface px-2 py-0.5 pr-6 font-mono text-xs text-zinc-200 placeholder:text-zinc-600 focus:border-accent focus:outline-none"
                />
                <button
                  type="button"
                  tabIndex={-1}
                  onClick={() => setCollectionDropdownOpen(!collectionDropdownOpen)}
                  className="absolute right-1 top-1/2 -translate-y-1/2 rounded p-0.5 text-zinc-600 hover:text-zinc-400"
                >
                  <ChevronDown size={10} />
                </button>
                {collectionDropdownOpen && mongoCollectionNames.length > 0 && (
                  <div className="absolute left-0 top-full z-50 mt-1 max-h-60 w-full overflow-auto rounded border border-zinc-700 bg-surface shadow-lg">
                    {mongoCollectionNames
                      .filter((name) => !mongoCollection || name.toLowerCase().includes(mongoCollection.toLowerCase()))
                      .map((name) => (
                        <button
                          key={name}
                          type="button"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => {
                            setMongoCollection(name);
                            persistOperationConfig({
                              kind: "mongo",
                              collection: name,
                              operation: mongoOperation,
                            });
                            setCollectionDropdownOpen(false);
                          }}
                          className={`w-full px-2 py-1 text-left font-mono text-xs hover:bg-surface-overlay ${name === mongoCollection ? "bg-surface-overlay text-zinc-100" : "text-zinc-300"}`}
                        >
                          {name}
                        </button>
                      ))}
                  </div>
                )}
              </div>
              {/* Schema refresh */}
              <button
                type="button"
                disabled={isRefreshingCatalog || !activeServerId}
                onClick={async () => {
                  if (!activeServerId) return;
                  setIsRefreshingCatalog(true);
                  try {
                    await introspectSchema(activeServerId);
                  } finally {
                    setIsRefreshingCatalog(false);
                  }
                }}
                className="rounded p-1 text-zinc-600 hover:bg-surface-overlay hover:text-zinc-300 disabled:opacity-40"
                title={mongoCollectionNames.length > 0 ? t("Refresh catalog") : t("Explore schema to get collection suggestions")}
              >
                <RefreshCw size={12} className={isRefreshingCatalog ? "animate-spin" : ""} />
              </button>
              <div className="relative">
                <select
                  value={mongoOperation}
                  onChange={(event) => {
                    const next = event.target.value as MongoOperation;
                    setMongoOperation(next);
                    persistOperationConfig({
                      kind: "mongo",
                      collection: mongoCollection,
                      operation: next,
                    });
                  }}
                  className="appearance-none rounded border border-zinc-700 bg-surface py-0.5 pl-2 pr-6 text-xs text-zinc-300 hover:border-zinc-500 focus:border-accent focus:outline-none"
                >
                  <option value="find">find</option>
                  <option value="findOne">findOne</option>
                  <option value="aggregate">aggregate</option>
                  <option value="replaceById">replaceById</option>
                </select>
                <ChevronDown size={10} className="pointer-events-none absolute right-1.5 top-1/2 -translate-y-1/2 text-zinc-500" />
              </div>
            </>
          ) : (
            <>
              <span className="rounded border border-zinc-700 bg-surface px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-zinc-400">
                SQL
              </span>
              <input
                type="text"
                value={sqlDefaultSchema}
                onChange={(event) => {
                  const next = event.target.value;
                  setSqlDefaultSchema(next);
                  persistOperationConfig({
                    kind: "sql",
                    ...(next.trim() ? { defaultSchema: next.trim() } : {}),
                  });
                }}
                placeholder={t("default schema")}
                className="min-w-[10rem] flex-1 rounded border border-zinc-700 bg-surface px-2 py-0.5 font-mono text-xs text-zinc-200 placeholder:text-zinc-600 focus:border-accent focus:outline-none"
              />
            </>
          )}
        </div>
      )}

      {/* Tab bar */}
      <div className="flex shrink-0 items-center justify-between border-b border-border bg-surface-raised px-2">
        <div className="flex items-center">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                activeTab === tab.id
                  ? "border-b-2 border-brand text-zinc-100"
                  : "text-zinc-500 hover:text-zinc-300"
              }`}
            >
              {tab.label}
            </button>
          ))}

          {/* Content-Type selector for REST body tab */}
          {isRest && activeTab === "query" && hasBody && (
            <div className="relative ml-2">
              <select
                value={restContentType}
                onChange={(e) => { setRestContentType(e.target.value as RestContentType); scheduleAutoSave(); }}
                className="appearance-none rounded border border-zinc-700 bg-surface py-0.5 pl-2 pr-5 text-[10px] text-zinc-400 cursor-pointer hover:border-zinc-500 focus:border-accent focus:outline-none"
              >
                {CONTENT_TYPES.map((ct) => (
                  <option key={ct.value} value={ct.value}>{ct.label}</option>
                ))}
              </select>
              <ChevronDown size={8} className="pointer-events-none absolute right-1 top-1/2 -translate-y-1/2 text-zinc-600" />
            </div>
          )}
        </div>

        <div className="flex items-center gap-1.5">
          {/* History navigation */}
          <div className="flex items-center rounded border border-zinc-700 transition-colors">
            <button
              onClick={navigateHistoryBack}
              disabled={!canGoBack}
              className="rounded-l p-1 text-zinc-400 hover:text-zinc-200 disabled:opacity-30 disabled:cursor-not-allowed"
              title={t("Step back through execution history")}
            >
              <ChevronLeft size={14} />
            </button>
            <span
              className={`min-w-[3rem] text-center text-[10px] font-medium ${
                isViewingHistory
                  ? "bg-brand-dim px-1.5 py-0.5 text-brand-light"
                  : "text-zinc-600"
              }`}
            >
              {historyPositionLabel}
            </span>
            <button
              onClick={navigateHistoryForward}
              disabled={!canGoForward}
              className="rounded-r p-1 text-zinc-400 hover:text-zinc-200 disabled:opacity-30 disabled:cursor-not-allowed"
              title={t("Step forward through execution history")}
            >
              <ChevronRight size={14} />
            </button>
          </div>

          {/* Prettify */}
          {canPrettify && (
            <button
              onClick={handlePrettify}
              title={!isRest && activeTab === "query" ? "Format query — ⌘⇧F" : "Format JSON — ⌘⇧F"}
              className="rounded border border-zinc-700 p-1 text-zinc-400 transition-colors hover:border-zinc-500 hover:text-zinc-200"
            >
              <Braces size={14} />
            </button>
          )}

          {/* Execute / Cancel */}
          {isExecuting ? (
            <button
              onClick={cancelQuery}
              className="flex items-center gap-1.5 rounded bg-danger-strong px-3 py-1 text-xs font-semibold text-on-danger hover:bg-danger-strong-hover"
            >
              <X size={12} />
              Cancel
              {elapsedSeconds !== null && (
                <span className="ml-0.5 font-mono text-[10px] font-normal opacity-80">
                  {elapsedSeconds}s
                </span>
              )}
            </button>
          ) : (
            <button
              onClick={handleExecute}
              disabled={!activeServerId || (!isRest && !queryText.trim())}
              className={`flex items-center gap-1.5 rounded px-3 py-1 text-xs font-semibold disabled:cursor-not-allowed disabled:opacity-40 ${
                isProd
                  ? "border border-danger/40 bg-warning text-zinc-950 hover:border-danger hover:bg-warning/90"
                  : "bg-brand text-zinc-900 hover:bg-brand-light"
              }`}
            >
              <Play size={12} />
              {isRest ? "Send" : "Execute"}
              <kbd className={`ml-1 rounded px-1 py-0.5 text-[9px] font-normal ${
                isProd ? "bg-danger-bg text-danger" : "bg-brand-dim text-brand-light"
              }`}>
                ⌘↩
              </kbd>
            </button>
          )}
        </div>
      </div>

      {/* Editor area */}
      {isRest && activeTab === "params" ? (
        <QueryParamsGrid
          params={restParams}
          onChange={(params) => { setRestParams(params); scheduleAutoSave(); }}
        />
      ) : isMongo && activeTab === "variables" ? (
        <MongoVariablesPane queryText={queryText} />
      ) : isMongo && activeTab === "options" ? (
        <MongoOptionsPane
          operation={mongoOperation}
          options={mongoOptions}
          onChange={handleMongoOptionsChange}
          indexNames={mongoIndexNames}
        />
      ) : isSql && activeTab === "params" ? (
        <SqlParamsPane queryText={queryText} />
      ) : isSql && activeTab === "options" ? (
        <SqlOptionsPane
          options={{ ...sqlOptions, defaultSchema: sqlDefaultSchema || undefined }}
          onChange={handleSqlOptionsChange}
          schemaNames={sqlSchemaNames}
        />
      ) : isRest && activeTab === "query" && !hasBody ? (
        <div className="flex flex-1 items-center justify-center text-zinc-600 text-sm">
          {t("No body for {method} requests", { method: restMethod })}
        </div>
      ) : isMongo && activeTab === "query" ? (
        <MongoEditor
          value={queryText}
          onChange={(v) => { setQueryText(v); scheduleAutoSave(); }}
          placeholder={'{ field: "value" }'}
          onExecute={handleExecute}
          collection={mongoCollection}
          operation={mongoOperation}
        />
      ) : isSql && activeTab === "query" ? (
        <SqlEditor
          value={queryText}
          onChange={(v) => { setQueryText(v); scheduleAutoSave(); }}
          placeholder="SELECT * FROM table WHERE id = $1"
          onExecute={handleExecute}
          defaultSchema={sqlDefaultSchema}
        />
      ) : (
        <GraphQLEditor
          value={currentValue}
          onChange={handleChange}
          mode={editorMode}
          placeholder={placeholder}
          onExecute={handleExecute}
        />
      )}
      {activeQuery && !activeQuery.systemPreset ? (
        <QueryRiskBar
          remarks={queryRemarks}
          riskLevel={queryRiskLevel}
          isProduction={isProd}
          onRemarksChange={handleRemarksChange}
          onRiskLevelChange={handleRiskLevelChange}
        />
      ) : isProd ? <div className="hazard-stripe-h" /> : null}

      {showSaveDialog && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/55 px-4 backdrop-blur-sm"
          onClick={(event) => {
            if (event.target === event.currentTarget) {
              setShowSaveDialog(false);
            }
          }}
        >
          <div className="w-full max-w-md rounded-2xl border border-border bg-surface-raised shadow-2xl">
            <div className="border-b border-border px-5 py-4">
              <div className="text-lg font-semibold text-zinc-100">{t("Save Query")}</div>
              <div className="mt-1 text-sm text-zinc-400">{t("Create a saved query from the current editor state.")}</div>
            </div>

            <div className="space-y-4 px-5 py-4">
              <div>
                <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-zinc-500">{t("Name")}</label>
                <input
                  autoFocus
                  value={saveName}
                  onChange={(event) => setSaveName(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" && saveName.trim() && (saveCategorySelection !== NEW_CATEGORY_OPTION || saveCategory.trim())) {
                      event.preventDefault();
                      void handleSaveQuery();
                    }
                  }}
                  placeholder={requestType === "rest" ? t("Customer REST Lookup") : t("Customer Search")}
                  className="w-full rounded-lg border border-zinc-700 bg-surface px-3 py-2 text-sm text-zinc-200 outline-none placeholder:text-zinc-600 focus:border-accent"
                />
              </div>

              <div>
                <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-zinc-500">{t("Save To")}</label>
                <select
                  value={saveCategorySelection}
                  onChange={(event) => setSaveCategorySelection(event.target.value)}
                  className="w-full rounded-lg border border-zinc-700 bg-surface px-3 py-2 text-sm text-zinc-200 outline-none focus:border-accent"
                >
                  <option value={NEW_CATEGORY_OPTION}>{t("New category")}</option>
                  {savedCategoryOptions.map((category) => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
              </div>

              {saveCategorySelection === NEW_CATEGORY_OPTION && (
                <div>
                  <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-zinc-500">{t("Category")}</label>
                  <input
                    value={saveCategory}
                    onChange={(event) => setSaveCategory(event.target.value)}
                    placeholder={t("Saved")}
                    className="w-full rounded-lg border border-zinc-700 bg-surface px-3 py-2 text-sm text-zinc-200 outline-none placeholder:text-zinc-600 focus:border-accent"
                  />
                </div>
              )}

              <div>
                <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-zinc-500">{t("Risk")}</label>
                <select
                  value={saveRiskLevel}
                  onChange={(event) => setSaveRiskLevel(event.target.value as QueryRiskLevel)}
                  className="w-full rounded-lg border border-zinc-700 bg-surface px-3 py-2 text-sm text-zinc-200 outline-none focus:border-accent"
                >
                  {QUERY_RISK_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
                <div className="mt-1 text-xs text-zinc-500">
                  {QUERY_RISK_OPTIONS.find((option) => option.value === saveRiskLevel)?.note}
                </div>
              </div>

              <div>
                <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-zinc-500">{t("Remarks")}</label>
                <textarea
                  value={saveRemarks}
                  onChange={(event) => setSaveRemarks(event.target.value)}
                  placeholder={t("Short guidance for agents and future you.")}
                  rows={4}
                  className="w-full rounded-lg border border-zinc-700 bg-surface px-3 py-2 text-sm text-zinc-200 outline-none placeholder:text-zinc-600 focus:border-accent"
                />
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 border-t border-border px-5 py-4">
              <button
                onClick={() => setShowSaveDialog(false)}
                className="rounded-lg border border-border px-4 py-2 text-sm text-zinc-300 hover:bg-surface"
              >
                {t("Cancel")}
              </button>
              <button
                onClick={() => void handleSaveQuery()}
                disabled={!saveName.trim() || (saveCategorySelection === NEW_CATEGORY_OPTION && !saveCategory.trim())}
                className="rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-zinc-950 hover:bg-brand-light disabled:cursor-not-allowed disabled:opacity-40"
              >
                {isSavingCopy ? t("Save Copy") : t("Save Query")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
